/**
 * The product lines a deployment can be for, and the one place that knows which family each belongs to.
 *
 * Nothing else compares a product against "Traverse" or "Process Pro" text: it asks `isTraverse` / `isProcessPro`, which read
 * the family from here. The product chosen in the wizard's first step (the catalog's `traverse` and `process-pro` products)
 * is recognised too, so a product that has no line chosen yet still resolves to its family.
 */

export type ProductFamily = 'traverse' | 'process-pro'

export type ProductLineId = 'traverse-11.0-standard' | 'traverse-11.2-global' | 'process-pro-global'

export interface ProductLine {
  id: ProductLineId
  /** What the user sees. */
  label: string
  family: ProductFamily
  /**
   * What the Traverse deployment workflow receives as its `product_line` input. It is the value the workflow has always been
   * sent, so choosing a new line label does not change what the pipeline is given.
   */
  pipelineValue: string
  /**
   * The Azure region (its code, for example `northcentralus`) most customers of this line are in. The Location step chooses it
   * when the environment offers it, so the user only changes it for the exception. None when the line has no usual region.
   */
  defaultRegion?: string
}

const line = (value: ProductLine): ProductLine => Object.freeze(value)

/** Where most Traverse customers' resource groups are. */
const TRAVERSE_REGION = 'northcentralus'

/** The product lines, in the order they are offered. Exactly these three. Each is frozen, so none can be changed by a reader. */
export const PRODUCT_LINES: readonly ProductLine[] = Object.freeze([
  line({ id: 'traverse-11.0-standard', label: 'Traverse - 11.0 Standard', family: 'traverse', pipelineValue: 'Traverse', defaultRegion: TRAVERSE_REGION }),
  line({ id: 'traverse-11.2-global', label: 'Traverse - 11.2 Global', family: 'traverse', pipelineValue: 'Traverse Global', defaultRegion: TRAVERSE_REGION }),
  line({ id: 'process-pro-global', label: 'Process Pro Global', family: 'process-pro', pipelineValue: 'ProcessPro Global' }),
])

/** The line a Traverse deployment starts with. */
export const DEFAULT_PRODUCT_LINE_ID: ProductLineId = 'traverse-11.0-standard'

export function getProductLine(id: ProductLineId): ProductLine {
  const line = PRODUCT_LINES.find((item) => item.id === id)
  if (!line) throw new Error(`Unknown product line: ${id}`)
  return line
}

export function isProductLineId(value: unknown): value is ProductLineId {
  return PRODUCT_LINES.some((line) => line.id === value)
}

/** A line's label for display. A value that is not a line (for example one stored by an older version) is shown as it is. */
export function productLineLabel(id: string): string {
  return PRODUCT_LINES.find((line) => line.id === id)?.label ?? id
}

/** What the Traverse deployment workflow is sent as `product_line` for a line. A value that is not a line is sent unchanged. */
export function pipelineValueOf(id: string): string {
  return PRODUCT_LINES.find((line) => line.id === id)?.pipelineValue ?? id
}

/**
 * True for one of this catalog's lines. A catalog product (Ross, AppCentral) has a `family` of its own, so a `family` property
 * does not say "line"; a line is recognised by its own id and its pipeline value.
 */
export function isProductLine(value: unknown): value is ProductLine {
  if (typeof value !== 'object' || value === null) return false
  const candidate = value as Partial<ProductLine>
  return typeof candidate.pipelineValue === 'string' && isProductLineId(candidate.id)
}

/** Anything that can be asked which family it is: a line, a line id, or a product that may carry a line. */
export type ProductSubject =
  | ProductLine
  | ProductLineId
  | { slug?: string; name?: string; line?: ProductLineId }
  | string
  | null
  | undefined

/** A catalog product's slug or name, lower-cased with everything but letters and digits removed. */
function key(value: string | undefined): string {
  return (value ?? '').toLowerCase().replace(/[^a-z0-9]/g, '')
}

// The products of the wizard's first step, by key, and the family each one is.
const PRODUCT_FAMILY_BY_KEY: Readonly<Record<string, ProductFamily>> = Object.freeze({
  traverse: 'traverse',
  processpro: 'process-pro',
})

/** The family of a subject, or undefined when it belongs to none (Ross, AppCentral, nothing chosen). */
export function productFamily(subject: ProductSubject): ProductFamily | undefined {
  if (!subject) return undefined
  if (typeof subject === 'string') {
    return isProductLineId(subject) ? getProductLine(subject).family : PRODUCT_FAMILY_BY_KEY[key(subject)]
  }
  if (isProductLine(subject)) return subject.family
  if (subject.line && isProductLineId(subject.line)) return getProductLine(subject.line).family
  return PRODUCT_FAMILY_BY_KEY[key(subject.slug)] ?? PRODUCT_FAMILY_BY_KEY[key(subject.name)]
}

export const isTraverse = (subject: ProductSubject): boolean => productFamily(subject) === 'traverse'
export const isProcessPro = (subject: ProductSubject): boolean => productFamily(subject) === 'process-pro'

/** True for a product that is one of the product lines: the three whose questions and sizing this catalog drives. */
export const hasProductLine = (subject: ProductSubject): boolean => productFamily(subject) !== undefined

/**
 * The line of a subject: the line it carries, else the Process Pro line for the Process Pro product, else the default
 * Traverse line for the Traverse product. Undefined for a product that has none.
 */
export function productLineOf(subject: ProductSubject): ProductLine | undefined {
  if (!subject) return undefined
  if (isProductLine(subject)) return subject
  const carried = typeof subject === 'string' ? (isProductLineId(subject) ? subject : undefined) : subject.line
  if (carried) return getProductLine(carried)
  const family = productFamily(subject)
  if (family === 'process-pro') return getProductLine('process-pro-global')
  if (family === 'traverse') return getProductLine(DEFAULT_PRODUCT_LINE_ID)
  return undefined
}

/** The region code a subject's product line is usually deployed to (Traverse: North Central US), or undefined when it has none. */
export const defaultRegionOf = (subject: ProductSubject): string | undefined => productLineOf(subject)?.defaultRegion

/** The catalog slug of a family: what the infrastructure lookups and naming rules are keyed by. */
export const FAMILY_SLUG: Readonly<Record<ProductFamily, string>> = Object.freeze({ traverse: 'traverse', 'process-pro': 'process-pro' })

/** The product the Infrastructure flow is for, given a line: its family's slug, the line's own name, and the line itself. */
export function infraProductForLine(id: ProductLineId): { slug: string; name: string; line: ProductLineId } {
  const line = getProductLine(id)
  return { slug: FAMILY_SLUG[line.family], name: line.label, line: line.id }
}
